import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'

import { cn, variants } from '@/lib/utils'

const positioner = variants('z-50')
const popup = variants('max-w-xs px-3 py-1.5 origin-(--transform-origin)')
/*
  Webfluential tones. `default` is the app's card-style tooltip, `brand` the side menu's,
  `plain` the uncoloured AppTooltip bubble.
  Any other tone is a colour from the app's `.badge-tooltip.<colour>` classes
  (components/elements.css); those set the text colour to the bubble colour so the arrow
  matches, and the caller colours the label.
*/
const tones = variants({
  default: 'rounded-md border border-border bg-background text-base text-foreground shadow-md',
  brand: 'rounded-md bg-primary-700 text-base text-primary-200 shadow-sm',
  plain: 'badge-tooltip bg-background text-foreground',
})
const arrowTones = variants({
  default: 'fill-background',
  brand: 'fill-primary-700',
  plain: 'fill-background',
  badge: 'fill-current',
})
const arrow = variants(
  'flex data-[side=bottom]:-top-1.25 data-[side=top]:-bottom-1.25 data-[side=top]:rotate-180 data-[side=left]:-right-2 data-[side=left]:rotate-90 data-[side=right]:-left-2 data-[side=right]:-rotate-90'
)
const popupAnimation = variants(
  'data-[side=bottom]:data-starting-style:-translate-y-0.5 data-[side=top]:data-starting-style:translate-y-0.5 data-[side=bottom]:data-ending-style:-translate-y-0.5 data-[side=top]:data-ending-style:translate-y-0.5 data-[side=right]:data-starting-style:-translate-x-0.5 data-[side=right]:data-ending-style:-translate-x-0.5 data-[side=left]:data-starting-style:translate-x-0.5 data-[side=left]:data-ending-style:translate-x-0.5 transition-appear duration-fast ease-out data-ending-style:scale-97 data-ending-style:opacity-0 data-instant:transition-none data-starting-style:scale-97 data-starting-style:opacity-0 motion-reduce:transition-opacity'
)

type TooltipProviderProps = TooltipPrimitive.Provider.Props

/** Put once near the app root: shares the open delay across tooltips. */
function TooltipProvider({ delay = 500, ...props }: TooltipProviderProps) {
  return <TooltipPrimitive.Provider delay={delay} {...props} />
}

/** Root. Opens on hover and focus. */
const Tooltip = TooltipPrimitive.Root

/** Renders a `<button type="button">` (so it never submits a form), or the `render` element. */
function TooltipTrigger(props: TooltipPrimitive.Trigger.Props) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      type={props.render ? undefined : 'button'}
      {...props}
    />
  )
}

type Placement = Pick<
  TooltipPrimitive.Positioner.Props,
  'side' | 'align' | 'sideOffset' | 'alignOffset'
>

type TooltipContentProps = TooltipPrimitive.Popup.Props &
  Placement & {
    /** All positioner options. `side`/`align`/offsets can also be passed directly. */
    positionerProps?: TooltipPrimitive.Positioner.Props
    /** `default`, `brand`, `plain`, or a `.badge-tooltip` colour (`primary`, `danger`, …). */
    tone?: string
    /** Show a pointer towards the trigger. */
    arrow?: boolean
  }

/** Portal + positioner + popup. */
function TooltipContent({
  className,
  positionerProps,
  side,
  align,
  sideOffset = 6,
  alignOffset,
  tone = 'default',
  arrow: withArrow = false,
  children,
  ...props
}: TooltipContentProps) {
  const isBadge = !Object.hasOwn(tones, tone)
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        className={positioner}
        {...positionerProps}
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            popup,
            isBadge ? `badge-tooltip ${tone}` : tones[tone as keyof typeof tones],
            popupAnimation,
            className
          )}
          {...props}
        >
          {withArrow && (
            <TooltipPrimitive.Arrow
              className={cn(
                arrow,
                arrowTones[isBadge ? 'badge' : (tone as keyof typeof tones)]
              )}
            >
              <svg width="10" height="5" viewBox="0 0 30 10" aria-hidden="true">
                <polygon points="0,10 30,10 15,0" />
              </svg>
            </TooltipPrimitive.Arrow>
          )}
          {children}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  type TooltipProviderProps,
  type TooltipContentProps,
}
